import React, { useState, useEffect, useRef, useCallback } from "react";
import { Save, ChevronDown, Bot, Database, Eye, Check } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";

const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div ref={dropdownRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-3.5 py-2.5 bg-white border text-left text-xs font-medium rounded-xl transition-all flex items-center justify-between outline-none ${
          disabled
            ? "opacity-40 cursor-not-allowed bg-zinc-50/50 border-zinc-200"
            : isOpen
            ? "border-zinc-900 shadow-sm ring-1 ring-zinc-900/5 bg-white"
            : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50/40 text-zinc-900 cursor-pointer"
        }`}
      >
        <span className={`truncate ${selectedOption ? "text-zinc-900 font-medium" : "text-zinc-400"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 transition-transform duration-200 text-zinc-400 ml-2 ${isOpen ? "rotate-180 text-zinc-900" : ""}`}
        />
      </button>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1.5 py-1 bg-white border border-zinc-200/90 rounded-xl max-h-60 overflow-y-auto z-50 shadow-lg shadow-zinc-950/5 custom-scrollbar animate-fadeIn">
          {options.length === 0 ? (
            <div className="px-3 py-2.5 text-xs text-zinc-400 text-center">No options available</div>
          ) : (
            options.map((option) => {
              const isSelected = value === option.value;
              return (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-3.5 py-2 text-xs text-left flex items-center justify-between transition-colors ${
                    isSelected
                      ? "bg-zinc-100 text-zinc-900 font-semibold"
                      : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && <Check size={13} className="text-zinc-900 shrink-0 ml-2" />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const UNCHANGED_KEY = "__UNCHANGED__";

const CHAT_MODELS = [
  // OpenAI
  { value: "gpt-4o", label: "GPT-4o (Multimodal)", provider: "openai" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini (Cost-Efficient)", provider: "openai" },
  { value: "gpt-4", label: "GPT-4 (Reliable Capable)", provider: "openai" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo (Fast)", provider: "openai" },
  { value: "gpt-4.1", label: "GPT-4.1 (Non-Reasoning)", provider: "openai" },
  { value: "gpt-5-mini", label: "GPT-5 Mini (Fast & Efficient)", provider: "openai" },
  { value: "gpt-5-nano", label: "GPT-5 Nano (Ultra Fast)", provider: "openai" },
  { value: "gpt-5.1", label: "GPT-5.1 (Best for Agents)", provider: "openai" },
  { value: "o1-preview", label: "o1 Preview (Reasoning)", provider: "openai" },
  { value: "o1-mini", label: "o1 Mini (Fast Reasoning)", provider: "openai" },
  { value: "o3-mini", label: "o3-mini (Best for Coding)", provider: "openai" },
  { value: "gpt-oss-120b", label: "GPT OSS 120B (Open-Weight)", provider: "openai" },
  { value: "gpt-oss-20b", label: "GPT OSS 20B (High-Reasoning)", provider: "openai" },

  // Anthropic
  { value: "claude-3-5-sonnet-20240620", label: "Claude 3.5 Sonnet", provider: "anthropic" },
  { value: "claude-3-5-haiku-20241022", label: "Claude 3.5 Haiku", provider: "anthropic" },
  { value: "claude-3-haiku-20240307", label: "Claude 3 Haiku", provider: "anthropic" },
  { value: "claude-3-opus-20240229", label: "Claude 3 Opus", provider: "anthropic" },
  { value: "claude-4.1-opus", label: "Claude 4.1 Opus (Research)", provider: "anthropic" },
  { value: "claude-4.5-haiku", label: "Claude 4.5 Haiku (Instant)", provider: "anthropic" },
  { value: "claude-4.5-sonnet-20240620", label: "Claude 4.5 Sonnet (Coding)", provider: "anthropic" },

  // Google
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro (Advanced)", provider: "google" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash (Balanced)", provider: "google" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash-Lite (Fast)", provider: "google" },
  { value: "gemini-2.0-flash", label: "Gemini 2.0 Flash", provider: "google" },
  { value: "gemini-2.0-flash-lite", label: "Gemini 2.0 Flash-Lite", provider: "google" },
  { value: "gemini-pro-latest", label: "Gemini 1.5 Pro (Latest)", provider: "google" },
  { value: "gemini-flash-latest", label: "Gemini 1.5 Flash (Latest)", provider: "google" },
  { value: "gemini-pro", label: "Gemini Pro", provider: "google" },

  // Meta
  { value: "llama-3.3-70b-instruct", label: "Llama 3.3 70B (Text Only)", provider: "meta" },
  { value: "llama-3.1-405b-instruct", label: "Llama 3.1 405B (Massive)", provider: "meta" },
  { value: "llama-3.1-70b-instruct", label: "Llama 3.1 70B", provider: "meta" },
  { value: "llama-3.1-8b-instruct", label: "Llama 3.1 8B", provider: "meta" },
  { value: "llama-3.2-3b-instruct", label: "Llama 3.2 3B", provider: "meta" },
  { value: "llama-3.2-1b-instruct", label: "Llama 3.2 1B", provider: "meta" },
  { value: "Llama-4-Maverick-17B-128E-Instruct-FP8", label: "Llama 4 Maverick 17B", provider: "meta" },
  { value: "Llama-4-Scout-17B-16E-Instruct", label: "Llama 4 Scout 17B", provider: "meta" },

  // Mistral
  { value: "mistral-large-latest", label: "Mistral Large (Latest)", provider: "mistral" },
  { value: "mistral-large-2", label: "Mistral Large 2", provider: "mistral" },
  { value: "mistral-medium-3", label: "Mistral Medium 3", provider: "mistral" },
  { value: "mistral-small-latest", label: "Mistral Small", provider: "mistral" },
  { value: "mistral-small-3.1", label: "Mistral Small 3.1", provider: "mistral" },
  { value: "codestral-latest", label: "Codestral (Coding)", provider: "mistral" },
  { value: "mixtral-8x22b-instruct", label: "Mixtral 8x22B", provider: "mistral" },
];

const VISION_MODELS = [
  // OpenAI
  { value: "gpt-4o", label: "GPT-4o (Multimodal)", provider: "openai" },
  { value: "gpt-4o-mini", label: "GPT-4o Mini (Cost-Efficient)", provider: "openai" },
  // Google
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash (Balanced)", provider: "google" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro (Advanced)", provider: "google" },
  { value: "gemini-flash-latest", label: "Gemini 1.5 Flash (Latest)", provider: "google" },
  { value: "gemini-pro-latest", label: "Gemini 1.5 Pro (Latest)", provider: "google" },
];

const EMBEDDERS = [
  // OpenAI
  { value: "text-embedding-3-small", label: "Text Embedding 3 Small", provider: "openai" },
  { value: "text-embedding-3-large", label: "Text Embedding 3 Large", provider: "openai" },
  { value: "text-embedding-ada-002", label: "ADA-002 (Legacy)", provider: "openai" },

  // Google
  { value: "gemini-embedding-2", label: "Gemini Embedding 2 (Recommended)", provider: "google" },
  { value: "gemini-embedding-001", label: "Gemini Embedding 001", provider: "google" },

  // Cohere
  { value: "embed-english-v3.0", label: "Cohere English v3", provider: "cohere" },
  { value: "embed-multilingual-v3.0", label: "Cohere Multilingual v3", provider: "cohere" },

  // Others from legacy
  { value: "mistral-embed", label: "Mistral Embed", provider: "mistral" },
  { value: "jina-embeddings-v3", label: "Jina Embeddings v3", provider: "jina" },
  { value: "nomic-ai/nomic-embed-text-v1.5", label: "Nomic Text v1.5 (Fireworks)", provider: "fireworks" },
  { value: "text-search-doc/latest", label: "Nebius Search Doc", provider: "nebius" },
  { value: "text-search-query/latest", label: "Nebius Search Query", provider: "nebius" },
  { value: "togethercomputer/m2-bert-80M-32k-retrieval", label: "Together M2 BERT 32k", provider: "together" },
  { value: "togethercomputer/m2-bert-80M-8k-retrieval", label: "Together M2 BERT 8k", provider: "together" },
];

const TAB_ORDER = ["chat-models", "vision-models", "embedders"];

const ModelManagement = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("chat-models");

  const handleTabChange = (nextTab) => {
    if (nextTab === activeTab) return;
    setActiveTab(nextTab);
  };

  // Chat Models State
  const [selectedChatProvider, setSelectedChatProvider] = useState("");
  const [selectedChatModel, setSelectedChatModel] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  // Vision Models State
  const [selectedVisionProvider, setSelectedVisionProvider] = useState("");
  const [selectedVisionModel, setSelectedVisionModel] = useState("");
  const [visionLoading, setVisionLoading] = useState(false);

  // Embedders State
  const [selectedEmbedderProvider, setSelectedEmbedderProvider] = useState("");
  const [selectedEmbedderModel, setSelectedEmbedderModel] = useState("");
  const [embedderLoading, setEmbedderLoading] = useState(false);


  const fetchActiveModel = useCallback(async () => {
    try {
      const data = await ApiClient.request("/models/active");
      if (data) {
        setSelectedChatProvider(data.provider);
        setSelectedChatModel(data.model_id);
        setSelectedVisionProvider(data.vision_provider || "");
        setSelectedVisionModel(data.vision_model_id || "");
      }
    } catch (error) {
      console.error("Error fetching active model:", error);
    }
  }, []);

  const fetchActiveEmbedder = useCallback(async () => {
    try {
      const data = await ApiClient.request("/embedder/active");
      if (data) {
        setSelectedEmbedderProvider(data.provider);
        setSelectedEmbedderModel(data.model_id);
      }
    } catch (error) {
      console.error("Error fetching active embedder:", error);
    }
  }, []);

  useEffect(() => {
    fetchActiveModel();
    fetchActiveEmbedder();
  }, [activeAiAgentId, fetchActiveModel, fetchActiveEmbedder]);

  // Derived Grouped Options
  const groupedChatModels = CHAT_MODELS.reduce((acc, model) => {
    if (!acc[model.provider]) acc[model.provider] = [];
    acc[model.provider].push(model);
    return acc;
  }, {});

  const groupedVisionModels = VISION_MODELS.reduce((acc, model) => {
    if (!acc[model.provider]) acc[model.provider] = [];
    acc[model.provider].push(model);
    return acc;
  }, {});

  const groupedEmbedders = EMBEDDERS.reduce((acc, embedder) => {
    if (!acc[embedder.provider]) acc[embedder.provider] = [];
    acc[embedder.provider].push(embedder);
    return acc;
  }, {});

  const handleSaveChatModel = async (e) => {
    e.preventDefault();
    if (!selectedChatProvider || !selectedChatModel) return;

    setChatLoading(true);
    try {
      await ApiClient.request("/models/save-config", {
        method: "POST",
        body: JSON.stringify({
          provider: selectedChatProvider,
          model_id: selectedChatModel,
          api_key: UNCHANGED_KEY,
          vision_provider: selectedVisionProvider,
          vision_model_id: selectedVisionModel,
          vision_api_key: UNCHANGED_KEY,
        }),
      });

      showToast("Chat model configuration saved successfully!", "success");
    } catch (error) {
      console.error("Error saving chat model:", error);
      showToast("Failed to save chat model configuration", "error");
    } finally {
      setChatLoading(false);
    }
  };

  const handleSaveVisionModel = async (e) => {
    e.preventDefault();
    if (!selectedVisionProvider || !selectedVisionModel) return;

    setVisionLoading(true);
    try {
      await ApiClient.request("/models/save-config", {
        method: "POST",
        body: JSON.stringify({
          provider: selectedChatProvider,
          model_id: selectedChatModel,
          api_key: UNCHANGED_KEY,
          vision_provider: selectedVisionProvider,
          vision_model_id: selectedVisionModel,
          vision_api_key: UNCHANGED_KEY,
        }),
      });

      showToast("Vision model configuration saved successfully!", "success");
    } catch (error) {
      console.error("Error saving vision model:", error);
      showToast("Failed to save vision model configuration", "error");
    } finally {
      setVisionLoading(false);
    }
  };

  const handleSaveEmbedder = async (e) => {
    e.preventDefault();
    if (!selectedEmbedderProvider || !selectedEmbedderModel)
      return;

    setEmbedderLoading(true);
    try {
      await ApiClient.request("/embedder/save-config", {
        method: "POST",
        body: JSON.stringify({
          provider: selectedEmbedderProvider,
          model_id: selectedEmbedderModel,
          api_key: UNCHANGED_KEY,
        }),
      });

      showToast("Embedder configuration saved successfully!", "success");
    } catch (error) {
      console.error("Error saving embedder:", error);
      showToast("Failed to save embedder configuration", "error");
    } finally {
      setEmbedderLoading(false);
    }
  };

  const getProviderName = (provider) => {
    const names = {
      openai: "OpenAI",
      anthropic: "Anthropic",
      google: "Google",
      cohere: "Cohere",
      fireworks: "Fireworks",
      together: "Together",
      nebius: "Nebius",
      mistral: "Mistral",
      jina: "Jina",
      meta: "Meta",
    };
    return names[provider] || provider;
  };

  const getChatModelsForProvider = (provider) => {
    return groupedChatModels[provider] || [];
  };

  const getEmbeddersForProvider = (provider) => {
    return groupedEmbedders[provider] || [];
  };

  return (
    <div className="min-h-screen bg-white py-6 px-4 sm:py-8 sm:px-6 lg:py-10 lg:px-10 flex justify-center items-start">
      <div className="w-full max-w-4xl space-y-6 sm:space-y-8">
        {/* Minimal Header */}
        <div className="border-b border-zinc-100 pb-5 sm:pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold tracking-tight text-zinc-900">
              Models
            </h1>
            <p className="text-xs text-zinc-500 mt-1">
              Select and configure the language models powering your assistant.
            </p>
          </div>

          {/* Minimal Tab Switcher */}
          <div className="flex p-1 bg-zinc-100/80 rounded-xl border border-zinc-200/60 w-full sm:w-auto overflow-x-auto custom-scrollbar">
            <button
              type="button"
              onClick={() => handleTabChange("chat-models")}
              className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                activeTab === "chat-models"
                  ? "bg-white text-zinc-900 shadow-sm"
                  : "text-zinc-500 hover:text-zinc-900"
              }`}
            >
              <Bot size={13} />
              <span>Chat</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange("vision-models")}
              className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                activeTab === "vision-models"
                  ? "bg-white text-zinc-900 shadow-sm"
                  : "text-zinc-500 hover:text-zinc-900"
              }`}
            >
              <Eye size={13} />
              <span>Vision</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange("embedders")}
              className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                activeTab === "embedders"
                  ? "bg-white text-zinc-900 shadow-sm"
                  : "text-zinc-500 hover:text-zinc-900"
              }`}
            >
              <Database size={13} />
              <span>Embeddings</span>
            </button>
          </div>
        </div>

        {/* Minimal Content Card with horizontal slide track */}
        <div className="border border-zinc-200/80 rounded-2xl bg-white shadow-xs overflow-hidden w-full">
          <div
            className="flex transition-transform duration-300 ease-out items-start"
            style={{
              transform: `translateX(-${(TAB_ORDER.indexOf(activeTab) * 100) / 3}%)`,
              width: "300%",
            }}
          >
            {/* Panel 1: Chat Models */}
            <div className="w-1/3 flex-shrink-0 p-4 sm:p-6 md:p-8 box-border">
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-zinc-100">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 border border-purple-100 flex items-center justify-center shrink-0">
                    <Bot size={16} />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-zinc-900">Chat Model</h2>
                    <p className="text-xs text-zinc-400">Core reasoning engine for real-time conversations</p>
                  </div>
                </div>

                <form onSubmit={handleSaveChatModel} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">
                        Provider
                      </label>
                      <CustomDropdown
                        value={selectedChatProvider}
                        onChange={(value) => {
                          setSelectedChatProvider(value);
                          setSelectedChatModel("");
                        }}
                        options={Object.keys(groupedChatModels).map((provider) => ({
                          value: provider,
                          label: getProviderName(provider),
                        }))}
                        placeholder="Select Provider"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">
                        Model
                      </label>
                      <CustomDropdown
                        value={selectedChatModel}
                        onChange={setSelectedChatModel}
                        options={getChatModelsForProvider(selectedChatProvider).map((model) => ({
                          value: model.value,
                          label: model.label,
                        }))}
                        placeholder="Select Model"
                        disabled={!selectedChatProvider}
                      />
                    </div>
                  </div>

                  <div className="pt-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={chatLoading || !selectedChatProvider || !selectedChatModel}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-zinc-900 text-white rounded-xl text-xs font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shadow-xs"
                    >
                      {chatLoading ? (
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Save size={13} />
                      )}
                      <span>{chatLoading ? "Saving..." : "Save Changes"}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Panel 2: Vision Models */}
            <div className="w-1/3 flex-shrink-0 p-4 sm:p-6 md:p-8 box-border">
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-zinc-100">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 border border-purple-100 flex items-center justify-center shrink-0">
                    <Eye size={16} />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-zinc-900">Vision Model</h2>
                    <p className="text-xs text-zinc-400">Image analysis and multimodal understanding</p>
                  </div>
                </div>

                <form onSubmit={handleSaveVisionModel} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">
                        Provider
                      </label>
                      <CustomDropdown
                        value={selectedVisionProvider}
                        onChange={(value) => {
                          setSelectedVisionProvider(value);
                          setSelectedVisionModel("");
                        }}
                        options={Object.keys(groupedVisionModels).map((provider) => ({
                          value: provider,
                          label: getProviderName(provider),
                        }))}
                        placeholder="Select Provider"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">
                        Model
                      </label>
                      <CustomDropdown
                        value={selectedVisionModel}
                        onChange={setSelectedVisionModel}
                        options={(groupedVisionModels[selectedVisionProvider] || []).map((model) => ({
                          value: model.value,
                          label: model.label,
                        }))}
                        placeholder="Select Vision Model"
                        disabled={!selectedVisionProvider}
                      />
                    </div>
                  </div>

                  <div className="pt-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={visionLoading || !selectedVisionProvider || !selectedVisionModel}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-zinc-900 text-white rounded-xl text-xs font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shadow-xs"
                    >
                      {visionLoading ? (
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Save size={13} />
                      )}
                      <span>{visionLoading ? "Saving..." : "Save Changes"}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Panel 3: Embedders */}
            <div className="w-1/3 flex-shrink-0 p-4 sm:p-6 md:p-8 box-border">
              <div className="space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-zinc-100">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center shrink-0">
                    <Database size={16} />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-zinc-900">Embedding Model</h2>
                    <p className="text-xs text-zinc-400">Vector representation engine for RAG knowledge retrieval</p>
                  </div>
                </div>

                <form onSubmit={handleSaveEmbedder} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">
                        Provider
                      </label>
                      <CustomDropdown
                        value={selectedEmbedderProvider}
                        onChange={(value) => {
                          setSelectedEmbedderProvider(value);
                          setSelectedEmbedderModel("");
                        }}
                        options={Object.keys(groupedEmbedders).map((provider) => ({
                          value: provider,
                          label: getProviderName(provider),
                        }))}
                        placeholder="Select Provider"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-medium text-zinc-600 block">
                        Model
                      </label>
                      <CustomDropdown
                        value={selectedEmbedderModel}
                        onChange={setSelectedEmbedderModel}
                        options={getEmbeddersForProvider(selectedEmbedderProvider).map((embedder) => ({
                          value: embedder.value,
                          label: embedder.label,
                        }))}
                        placeholder="Select Embedder Model"
                        disabled={!selectedEmbedderProvider}
                      />
                    </div>
                  </div>

                  <div className="pt-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={embedderLoading || !selectedEmbedderProvider || !selectedEmbedderModel}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-zinc-900 text-white rounded-xl text-xs font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shadow-xs"
                    >
                      {embedderLoading ? (
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Save size={13} />
                      )}
                      <span>{embedderLoading ? "Saving..." : "Save Changes"}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelManagement;
